import { useEffect, useState, useCallback } from "react";
import { sfx } from "../../lib/soundEngine";

const LINES = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8],
    [0, 3, 6], [1, 4, 7], [2, 5, 8],
    [0, 4, 8], [2, 4, 6],
];

const winner = (b) => {
    for (const line of LINES) {
        const [a, c, d] = line;
        if (b[a] && b[a] === b[c] && b[a] === b[d]) return { player: b[a], line };
    }
    return null;
};

const findWinningMove = (b, player) => {
    for (let i = 0; i < 9; i++) {
        if (b[i]) continue;
        const copy = [...b];
        copy[i] = player;
        if (winner(copy)?.player === player) return i;
    }
    return -1;
};

// Rule-based AI: win > block > center > corner > side
const aiMove = (b) => {
    const win = findWinningMove(b, "O");
    if (win !== -1) return win;
    const block = findWinningMove(b, "X");
    if (block !== -1) return block;
    if (!b[4]) return 4;
    const corners = [0, 2, 6, 8].filter((i) => !b[i]);
    if (corners.length) return corners[Math.floor(Math.random() * corners.length)];
    const sides = [1, 3, 5, 7].filter((i) => !b[i]);
    return sides[Math.floor(Math.random() * sides.length)];
};

export const TicTacToeGame = () => {
    const [board, setBoard] = useState(Array(9).fill(null));
    const [xTurn, setXTurn] = useState(true);
    const [status, setStatus] = useState("YOUR TURN");
    const [done, setDone] = useState(false);
    const [winLine, setWinLine] = useState([]);

    const reset = useCallback(() => {
        setBoard(Array(9).fill(null));
        setXTurn(true);
        setStatus("YOUR TURN");
        setDone(false);
        setWinLine([]);
        sfx.coin();
    }, []);

    const clickCell = (i) => {
        if (done || board[i] || !xTurn) return;
        sfx.click();
        const next = [...board];
        next[i] = "X";
        setBoard(next);
        setXTurn(false);
    };

    useEffect(() => {
        const w = winner(board);
        if (w) {
            setDone(true);
            setWinLine(w.line);
            if (w.player === "X") { setStatus("YOU WIN!"); sfx.win(); }
            else { setStatus("AI WINS"); sfx.fail(); }
            return;
        }
        if (board.every(Boolean)) {
            setDone(true);
            setStatus("DRAW!");
            sfx.tick();
            return;
        }
        if (!xTurn) {
            setStatus("AI THINKING...");
            const t = setTimeout(() => {
                const move = aiMove(board);
                if (move === -1 || move === undefined) return;
                const next = [...board];
                next[move] = "O";
                setBoard(next);
                setXTurn(true);
                sfx.move();
                setStatus("YOUR TURN");
            }, 550);
            return () => clearTimeout(t);
        }
    }, [board, xTurn]);

    const statusColor = status.includes("WIN") && !status.includes("AI")
        ? "neon-cyan"
        : status.includes("AI WINS")
            ? "neon-pink"
            : status.includes("DRAW")
                ? "neon-amber"
                : xTurn ? "neon-cyan" : "neon-pink";

    return (
        <div className="flex flex-col gap-4">
            <div
                data-testid="ttt-status"
                className={`font-pixel text-xl text-center ${statusColor}`}
            >
                {status}
            </div>
            <div className="grid grid-cols-3 gap-2 mx-auto" style={{ width: "100%", maxWidth: 320 }}>
                {board.map((cell, i) => {
                    const color = cell === "X" ? "var(--neon-cyan)" : cell === "O" ? "var(--neon-pink)" : "transparent";
                    const isWinCell = winLine.includes(i);
                    return (
                        <button
                            key={i}
                            data-testid={`ttt-cell-${i}`}
                            className="ttt-cell"
                            onClick={() => clickCell(i)}
                            disabled={done || !!cell || !xTurn}
                            style={{
                                color,
                                textShadow: cell ? `0 0 8px ${color}, 0 0 20px ${color}` : "none",
                                borderColor: isWinCell ? color : "rgba(0, 240, 255, 0.35)",
                                boxShadow: isWinCell ? `0 0 16px ${color}` : "none",
                            }}
                        >
                            {cell || ""}
                        </button>
                    );
                })}
            </div>
            <div className="flex justify-center">
                <button onClick={reset} data-testid="ttt-reset-btn" className="btn-neon btn-neon-pink">
                    <i className="fa-solid fa-rotate-right" /> New Round
                </button>
            </div>
            <p className="text-xs text-gray-500 text-center font-body">
                You are <span className="neon-cyan">X</span> · AI is <span className="neon-pink">O</span>
            </p>
        </div>
    );
};
